import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCheckLimitStore, useExternalStore, useAccountsStore, useDictionariesStore, useNotificationStore } from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { RequestType, type GetRequisitesResponse } from '@shared/api';
import { ApprovePasswordForm } from './approvePasswordForm';
import { Button, Constants, ErrorMessage, Input, ItemsType, Modal, Select, Spinner, Textarea } from '@shared/widgets';

enum Step {
  Payment = 'payment',
  Password = 'password',
}

interface IForm {
  debitAccount?: ItemsType;
  amount?: string;
  currency?: ItemsType;
  paymentReference: string;
}
const DEFAULT_VALUES: IForm = { debitAccount: undefined, amount: '', currency: undefined, paymentReference: '' };

interface ExternalPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecipient?: GetRequisitesResponse;
  onShowSelectAccountModal: (value: boolean) => void;
}

export const ExternalPaymentsModal = (props: ExternalPaymentsModalProps) => {
  const accountStore = useAccountsStore();
  const dictionariesStore = useDictionariesStore();
  const checkLimitStore = useCheckLimitStore();
  const externalStore = useExternalStore();
  const notificationStore = useNotificationStore();

  const [step, setStep] = useState<Step>(Step.Payment);

  const { formState, control, handleSubmit, watch, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const debitAccount = watch('debitAccount');
  const amount = watch('amount');
  const currency = watch('currency');
  const paymentReference = watch('paymentReference');

  const cancelHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    const result = await checkLimitStore.checkLimit({
      requestType: RequestType.TransferOut,
      accountIdFrom: data.debitAccount?.value,
      accountIdTo: props.selectedRecipient?.Id,
      amount: +data.amount!,
      currency: data.currency?.value,
    });
    if (result.data?.LimitExceeded) {
      notificationStore.setError(result.data.Note);
      return;
    }

    const result2 = await externalStore.createExternal({
      AccountId: data.debitAccount?.value,
      Amount: +data.amount!,
      Currency: data.currency?.value,
      Reference: data.paymentReference,
      RequisitesId: props.selectedRecipient?.Id,
    });
    if (result2) setStep(Step.Password);
  };
  const openSelectModalHandler = () => {
    props.onShowSelectAccountModal(true);
    props.onClose();
  };
  const submitApprovePasswordHandler = async () => {
    props.onClose();
    setStep(Step.Payment);
    reset(DEFAULT_VALUES);
  };

  const currencyOptions = useMemo(
    () => dictionariesStore.data?.Currency?.map((item) => ({ value: item.Id, label: item.Name, leftLabel: item.Name })) || [],
    [dictionariesStore.data],
  );
  const accountOptions = useMemo(
    () => accountStore.data.map((item) => ({ value: item.Id, label: item.Amount, leftLabel: item.Currency })),
    [accountStore.data],
  );
  return (
    <Modal isOpen={props.isOpen} title='External payments'>
      <Spinner show={formState.isSubmitting} fullscreen />
      {step === Step.Payment && (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div className='flex flex-col gap-3'>
            <Controller
              name='debitAccount'
              defaultValue={accountOptions[0]}
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Debit Account</div>
                  <Select items={accountOptions} double value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <div className='mt-3'>
              <div className='text-xs font-semibold text-[#929292] uppercase'>Credit Accounts</div>
              <div className='flex h-[46px] cursor-pointer items-center justify-between border-b border-b-[#46413a]' onClick={openSelectModalHandler}>
                <div className='text-sm font-semibold text-[#929292] uppercase'>
                  {props.selectedRecipient?.OwnerName || 'Please select or add new recipient'}
                </div>
                <div>
                  <svg width='7' height='12' viewBox='0 0 7 12' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M6.654 6.59l-5.096 5.096a.58.58 0 0 1-.82 0l-.411-.41a.58.58 0 0 1 0-.822L4.6 6.18.327 1.906a.58.58 0 0 1 0-.821l.41-.41a.58.58 0 0 1 .821 0L6.654 5.77a.58.58 0 0 1 0 .82z'
                      fill='#929292'
                      fillRule='evenodd'
                    />
                  </svg>
                </div>
              </div>
            </div>
            <Controller
              name='amount'
              control={control}
              rules={{ required: 'Please enter Amount', maxLength: 100 }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Amount</div>
                  <Input
                    className='!h-[46px]'
                    variant='outlined'
                    rounded
                    type='number'
                    error={error?.message}
                    name='Amount'
                    placeholder='Enter Amount'
                    required
                    value={value}
                    onChange={(value) => AmountUtils.amountChange(value, onChange)}
                  />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
            <Controller
              name='currency'
              control={control}
              defaultValue={currencyOptions?.[0]}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Currency</div>
                  <Select items={currencyOptions} value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <Controller
              name='paymentReference'
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Payment Reference</div>
                  <Textarea variant='outlined' rounded placeholder='Enter reference' value={value} onChange={onChange} />
                </div>
              )}
            />
          </div>
          <div className='mt-6.5 flex items-center justify-between gap-2.5'>
            <Button className='h-[46px]' color='outlined' fullWidth onClick={cancelHandler}>
              Cancel
            </Button>
            <Button className='h-[46px]' fullWidth type='submit' disabled={!props.selectedRecipient || !formState.isValid || formState.isSubmitting}>
              Apply
            </Button>
          </div>
        </form>
      )}
      {step === Step.Password && (
        <ApprovePasswordForm
          data={[
            { title: 'Debit Account:', label: debitAccount?.leftLabel, value: AmountUtils.transformToAmount(debitAccount?.label) },
            { title: 'To:', label: props.selectedRecipient?.OwnerName },
            { title: 'Amount:', label: currency?.leftLabel, value: AmountUtils.transformToAmount(amount) },
            { title: 'Payment Reference:', label: paymentReference },
          ]}
          requestId={externalStore.data?.Id}
          onBack={() => setStep(Step.Payment)}
          onSubmit={submitApprovePasswordHandler}
        />
      )}
    </Modal>
  );
};
